import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, Calendar, Star, Heart, MapPin, User } from 'lucide-react';
import { useTestimonials } from '../hooks/useApi';

const Testimonials = () => {
  // Use API hook for testimonials
  const { testimonials, loading: testimonialsLoading } = useTestimonials();

  const averageRating = testimonials.length > 0 
    ? testimonials.reduce((acc, testimonial) => acc + testimonial.rating, 0) / testimonials.length
    : 5;
  const totalTestimonials = testimonials.length;

  return (
    <div>
      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-content">
          <div className="hero-announcement">
            <Heart size={16} className="text-red-500" />
            <span>Témoignages de nos patients</span>
          </div>
          
          <h1 className="heading-hero mb-4">
            Ce que disent nos patients
          </h1>
          
          <p className="body-large text-gray-700 max-w-2xl mx-auto mb-8">
            La satisfaction et le bien-être de nos patients sont au cœur de notre mission. 
            Découvrez leurs expériences avec PodioSoins+.
          </p>

          {/* Rating Summary */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-6">
            <div className="text-center">
              <div className="flex items-center justify-center mb-2">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} size={20} className="text-yellow-400" fill="currentColor" />
                ))}
              </div>
              <p className="heading-2">{averageRating.toFixed(1)}/5</p>
            </div>
            
            <div className="hidden sm:block w-px h-16 bg-gray-300"></div>
            
            <div className="text-center">
              <p className="heading-2">100%</p>
              <p className="body-small">Patients satisfaits</p>
              <p className="caption text-gray-600">Recommandent nos services</p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Grid */}
      <section className="py-16 bg-gray-50">
        <div className="container">
          {testimonialsLoading ? (
            <div className="text-center py-16">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="body-medium text-gray-600">Chargement des témoignages...</p>
            </div>
          ) : (
            <div className="grid lg:grid-cols-2 gap-8">
              {testimonials.map((testimonial) => (
                <div key={testimonial.id} className="service-card accent-cream hover-lift">
                  {/* Rating */}
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center space-x-1">
                      {[...Array(testimonial.rating)].map((_, i) => (
                        <Star key={i} size={16} className="text-yellow-400" fill="currentColor" />
                      ))}
                    </div>
                    <div className="px-3 py-1 bg-blue-100 rounded-full">
                      <p className="caption text-blue-800 font-medium">
                        {testimonial.service}
                      </p>
                    </div>
                  </div>

                  {/* Testimonial Text */}
                  <blockquote className="mb-6">
                    <p className="body-large text-gray-700 italic leading-relaxed">
                      "{testimonial.text}"
                    </p>
                  </blockquote>

                  {/* Patient Info */}
                  <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                        <User size={20} className="text-blue-600" />
                      </div>
                      <div>
                        <p className="body-small font-medium">{testimonial.name}</p>
                        <p className="caption text-gray-600">{testimonial.age}</p>
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-1 text-gray-500">
                      <MapPin size={14} />
                      <p className="caption">{testimonial.location}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Service Breakdown */}
      <section className="py-16 bg-white">
        <div className="container">
          <div className="text-center mb-12">
            <h2 className="heading-1 mb-4">Satisfaction par service</h2>
            <p className="body-large text-gray-600 max-w-2xl mx-auto">
              Nos patients apprécient la qualité de nos soins dans tous nos domaines d'expertise.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Wound Care */}
            <div className="service-card accent-blue text-center hover-lift">
              <div className="mb-4">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Heart size={32} className="text-blue-600" />
                </div>
                <h3 className="heading-2">Soins de plaies</h3>
              </div>
              
              <div className="flex items-center justify-center mb-3">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} size={16} className="text-yellow-400" fill="currentColor" />
                ))}
              </div>
              
              <p className="body-small text-gray-700 mb-4">
                Nos patients apprécient notre expertise dans le traitement des plaies 
                et notre approche préventive.
              </p>
              
              <div className="bg-blue-50 p-3 rounded-lg">
                <p className="caption text-blue-800">
                  "Professionnelle et rassurante" - Commentaire fréquent
                </p>
              </div>
            </div>

            {/* Foot Care */}
            <div className="service-card accent-green text-center hover-lift">
              <div className="mb-4">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Star size={32} className="text-green-600" />
                </div>
                <h3 className="heading-2">Soins de pieds</h3>
              </div>
              
              <div className="flex items-center justify-center mb-3">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} size={16} className="text-yellow-400" fill="currentColor" />
                ))}
              </div>
              
              <p className="body-small text-gray-700 mb-4">
                Soulagement immédiat et soins préventifs appréciés par tous nos patients.
              </p>
              
              <div className="bg-green-50 p-3 rounded-lg">
                <p className="caption text-green-800">
                  "Enfin quelqu'un qui comprend!" - Témoignage typique
                </p>
              </div>
            </div>

            {/* Specialized Care */}
            <div className="service-card accent-grey text-center hover-lift">
              <div className="mb-4">
                <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <User size={32} className="text-purple-600" />
                </div>
                <h3 className="heading-2">Soins spécialisés</h3>
              </div>
              
              <div className="flex items-center justify-center mb-3">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} size={16} className="text-yellow-400" fill="currentColor" />
                ))}
              </div>
              
              <p className="body-small text-gray-700 mb-4">
                Approche personnalisée et suivi attentionné hautement appréciés.
              </p>
              
              <div className="bg-purple-50 p-3 rounded-lg">
                <p className="caption text-purple-800">
                  "Service à domicile impeccable" - Retour constant
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Indicators */}
      <section className="py-16 bg-gray-50">
        <div className="container">
          <div className="text-center mb-12">
            <h2 className="heading-1 mb-4">Pourquoi nous faire confiance ?</h2>
            <p className="body-large text-gray-600 max-w-2xl mx-auto">
              Au-delà des témoignages, voici ce qui fait la différence PodioSoins+.
            </p>
          </div>

          <div className="grid md:grid-cols-4 gap-8">
            <div className="text-center space-y-3">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto">
                <Heart size={32} className="text-blue-600" />
              </div>
              <h3 className="heading-3">10+ ans</h3>
              <p className="body-small text-gray-600">D'expérience professionnelle</p>
            </div>

            <div className="text-center space-y-3">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                <User size={32} className="text-green-600" />
              </div>
              <h3 className="heading-3">OIIQ</h3>
              <p className="body-small text-gray-600">Membre certifiée</p>
            </div>

            <div className="text-center space-y-3">
              <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto">
                <Star size={32} className="text-yellow-600" />
              </div>
              <h3 className="heading-3">2</h3>
              <p className="body-small text-gray-600">Spécialités principales</p>
            </div>

            <div className="text-center space-y-3">
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto">
                <MapPin size={32} className="text-purple-600" />
              </div>
              <h3 className="heading-3">Longueuil</h3>
              <p className="body-small text-gray-600">Service à domicile</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-blue-50 to-green-50">
        <div className="container text-center">
          <div className="max-w-3xl mx-auto space-y-6">
            <h2 className="heading-1">Rejoignez nos patients satisfaits</h2>
            <p className="body-large text-gray-700">
              Découvrez pourquoi tant de personnes nous font confiance pour leurs soins de santé. 
              Votre bien-être mérite les meilleurs soins.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Link to="/contact" className="btn-primary hover-scale">
                <Calendar size={16} className="mr-2" />
                <span className="button-text">Prendre rendez-vous</span>
              </Link>
              <a href="tel:819-469-4419" className="btn-secondary hover-scale">
                <Phone size={16} className="mr-2" />
                <span className="button-text">Appeler</span>
              </a>
            </div>
            
            <p className="caption text-gray-600">
              Première consultation offerte • Sans engagement
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Testimonials;